/**
 * cart-coupon-utils.js
 *
 * cart-coupon-hub.js 의 순수 헬퍼 추출 모듈 (this.state 미참조).
 * IIFE 로 window.CartCouponHubUtils 에 attach. main 파일보다 먼저 로드되어야 함.
 *
 * 로드 순서 (sections/main-cart.liquid + snippets/cart-drawer.liquid):
 *   1. cart-coupon-utils.js  ← 본 파일
 *   2. cart-coupon-hub-mocks.js (옵션 — mock 모드만)
 *   3. cart-coupon-hub.js
 *
 * 회귀 박제: docs/policy/cart-coupon-hub.md 변경 이력 (2026-05-27 모듈 추출)
 */
(function (global) {
  'use strict';

  const Utils = {
    // ============ Money / Formatting ============

    // 🔴 normalizeBxgySelectableProduct 의 price 는 BE 응답이 number 인 경우 그대로. String 캐스팅 필수.
    parseMoney(text) {
      return parseFloat(String(text ?? '').replace(/[^\d.]/g, '')) || 0;
    },

    // 🔴 통화 기호 하드코딩 금지. window.theme.settings.money_format 는 Shopify Markets
    // 가 활성 market 의 포맷으로 자동 치환 (MO=MOP${{amount}}, HK=HK${{amount}} 등).
    // 전역 formatPrice(amount_in_cents, moneyString) 가 SSOT — Liquid `money` 필터와
    // 동일 출력 보장. 회귀 case: 2026-05-15 — Macao 선택 시 cart 총액만 HK$ 누수.
    formatMoney(n) {
      const moneyFormat = global.theme?.settings?.money_format || '${{amount}}';
      if (typeof global.formatPrice === 'function') {
        return global.formatPrice(Math.round(n * 100), moneyFormat);
      }
      return moneyFormat.replace(/\{\{.*?\}\}/, n.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
    },

    formatBxgyPrice(value) {
      const raw = String(value ?? '').trim();
      if (!raw) return '';
      if (/[^\d.,]/.test(raw)) return raw;
      return Utils.formatMoney(Utils.parseMoney(raw));
    },

    bxgyGetDiscountLabel(promo) {
      const discountType = String(
        promo?.getDiscountType || promo?.get_discount_type || promo?.mode || ''
      ).toLowerCase();
      const rawValue = promo?.getDiscountValue ?? promo?.get_discount_value ?? promo?.value;
      const value = Number(rawValue || 0);
      if ((discountType === 'fixed' || discountType === 'fixed_amount') && value > 0) {
        return `${Utils.formatMoney(value)} OFF`;
      }
      const percent = value > 0 ? value : 100;
      return percent >= 100 ? 'FREE' : `${percent}% OFF`;
    },

    // 🔴 (2026-05-28) BXGY 모달 혜택 안내 문구 (영문). Buy 행 아래 subtle 표시.
    //   "선택하면 무료인가?" 혼동 제거 — 혜택 종류(무료/할인) 를 명확히.
    //   count = 적용 대상 get 아이템 수 (required = applicationCount × getQty).
    bxgyBenefitMessage(promo, count = 1) {
      const n = Math.max(1, parseInt(count, 10) || 1);
      const itemWord = n > 1 ? `your ${n} selected items` : 'your selected item';
      const discountType = String(
        promo?.getDiscountType || promo?.get_discount_type || promo?.mode || ''
      ).toLowerCase();
      const value = Number(promo?.getDiscountValue ?? promo?.get_discount_value ?? promo?.value ?? 0);
      if ((discountType === 'fixed' || discountType === 'fixed_amount') && value > 0) {
        return `${Utils.formatMoney(value)} off will be applied to ${itemWord}.`;
      }
      const percent = value > 0 ? value : 100;
      if (percent >= 100) {
        return n > 1 ? `Your ${n} selected items will be free.` : 'Your selected item will be free.';
      }
      return `${percent}% off will be applied to ${itemWord}.`;
    },

    // 🔴 (2026-05-28) BXGY 그룹 식별자 = 진짜 UUID. variant 기반(ts-variantId) 폐기 — 같은 variant 다중 그룹 충돌 방지.
    //   confirm 1회 = 그룹 1개 = UUID 1개. anchor + 모든 gift 가 이 값을 공유.
    //   회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
    bxgyGenerateGroupId() {
      try {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
          return `bxgy-${crypto.randomUUID()}`;
        }
      } catch (_e) { /* fallthrough */ }
      return `bxgy-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    },

    escapeHtml(str) {
      return String(str).replace(/[&<>"']/g, (c) => {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    },

    // ============ Shopify ID ============

    toShopifyGid(type, id) {
      const raw = String(id || '').trim();
      if (!raw || raw.startsWith('gid://shopify/')) return raw;
      return `gid://shopify/${type}/${raw}`;
    },

    shopifyNumericId(id) {
      const raw = String(id || '').trim();
      if (!raw) return '';
      return raw.includes('/') ? raw.split('/').pop() : raw;
    },

    shopifyCartVariantId(id) {
      const numericId = Utils.shopifyNumericId(id);
      if (!numericId) return '';
      if (!/^\d+$/.test(numericId)) return numericId;
      const value = Number(numericId);
      return Number.isSafeInteger(value) ? value : numericId;
    },

    selectableVariantRawId(raw) {
      const variantList = raw.variants || raw.productVariants || raw.product_variants || [];
      const firstVariant = Array.isArray(variantList)
        ? variantList[0]
        : variantList?.edges?.[0]?.node;
      const candidates = [
        raw.variantId,
        raw.variant_id,
        raw.variantGid,
        raw.variant_gid,
        raw.defaultVariantId,
        raw.default_variant_id,
        raw.defaultVariantGid,
        raw.default_variant_gid,
        raw.variantAdminGraphqlApiId,
        raw.variant_admin_graphql_api_id,
        raw.variant?.id,
        raw.variant?.admin_graphql_api_id,
        raw.selectedVariant?.id,
        raw.selected_variant?.id,
        firstVariant?.id,
        firstVariant?.admin_graphql_api_id,
        raw.admin_graphql_api_id && String(raw.admin_graphql_api_id).includes('/ProductVariant/')
          ? raw.admin_graphql_api_id
          : '',
        raw.id && String(raw.id).includes('/ProductVariant/') ? raw.id : '',
      ];
      return candidates.find((value) => String(value || '').trim()) || '';
    },

    productHandleFromUrl(url) {
      const raw = String(url || '').trim();
      if (!raw) return '';
      const match = raw.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    },

    // ============ Storefront variant ============

    // 🔴 storefront 재고 정량 산출 (2026-05-27).
    //   inventory_management != 'shopify' → Shopify 가 재고 추적 안 함 → 무제한 (Infinity)
    //   inventory_policy == 'continue' → 재고 초과 판매 허용 → 무제한 (Infinity)
    //   그 외 → variant.inventory_quantity 가 상한 (0 미만은 0 으로 clamp)
    //   line-item.liquid:18-24 (max_quantity 산출) 와 동일 정책.
    resolveVariantStock(variant) {
      if (!variant) return Infinity;
      if (variant.inventory_management !== 'shopify') return Infinity;
      if (variant.inventory_policy === 'continue') return Infinity;
      const qty = Number(variant.inventory_quantity);
      return Number.isFinite(qty) ? Math.max(0, qty) : Infinity;
    },

    matchStorefrontVariant(item, variants) {
      const originalId = String(item.variantId || '');
      const sku = String(item.sku || '').trim();
      const barcode = String(item.barcode || '').trim();
      const option = String(item.option || '').trim();
      return variants.find((variant) => String(variant.id || '') === originalId) ||
        variants.find((variant) => sku && String(variant.sku || '').trim() === sku) ||
        variants.find((variant) => barcode && String(variant.barcode || '').trim() === barcode) ||
        variants.find((variant) => option && [
          variant.title,
          variant.option1,
          variant.option2,
          variant.option3,
        ].some((value) => String(value || '').trim() === option)) ||
        null;
    },

    // ============ Promotion normalization ============

    normalizePromotion(raw) {
      if (raw.type === 'bundle_fixed_price') return null;
      const buyQty = Number(raw.buyQty ?? raw.buy_qty ?? 0);
      const getQty = Number(raw.getQty ?? raw.get_qty ?? 0);
      const type = (raw.type === 'buy_x_get_y' || raw.type === 'bundle') && buyQty > 0 && getQty > 0
        ? 'buy_x_get_y'
        : 'amount_off';
      const valueType = raw.valueType || raw.value_type || raw.discountType || raw.mode || '';
      const value = Number(raw.value ?? raw.discountValue ?? raw.discount_value ?? 0);
      if (type === 'amount_off' && value <= 0) return null;
      const name = raw.name || raw.title || raw.code || raw.id || 'Promotion';
      return {
        ...raw,
        id: raw.id || raw.discountId || raw.discount_id || name,
        code: raw.code || raw.discountCode || raw.discount_code || raw.id || name,
        name,
        type,
        mode: valueType === 'percentage' ? 'percentage' : 'fixed',
        value,
        buyQty,
        getQty,
      };
    },

    normalizeBxgySelectableProduct(raw, index = 0) {
      const variantRawId = Utils.selectableVariantRawId(raw);
      const variantId = Utils.shopifyNumericId(variantRawId);
      if (!variantId) return null;
      // 🔴 FE 디펜시브 솔드아웃 필터 (2026-05-26) — BE 가 inventory_quantity > 0 거르지만
      // product_read_cache stale 시 솔드아웃이 응답에 새어들어옴. stock 필드 0 이하면 모달에서 제외.
      // 회귀 case: wiki/docs/bxgy-selectable-sold-out-fe-defense-2026-05-26.md
      const stock = Number(raw.stock ?? raw.inventoryQuantity ?? raw.inventory_quantity ?? Infinity);
      if (Number.isFinite(stock) && stock <= 0) return null;
      const variantGid = String(variantRawId || '').startsWith('gid://shopify/')
        ? String(variantRawId)
        : Utils.toShopifyGid('ProductVariant', variantId);
      const url = raw.productUrl || raw.product_url || raw.url || '';
      return {
        id: `catalog:${variantGid}:${index}`,
        source: 'catalog',
        variantId,
        variantGid,
        name: raw.name || raw.title || raw.productTitle || raw.product_title || variantGid,
        option: raw.variantTitle || raw.variant_title || raw.size || raw.option || '',
        handle: raw.handle || Utils.productHandleFromUrl(url),
        sku: raw.sku || raw.variantSku || raw.variant_sku || '',
        barcode: raw.barcode || raw.variantBarcode || raw.variant_barcode || '',
        quantity: 1,
        price: raw.price ?? raw.originalPrice ?? raw.original_price ?? '',
        image: raw.imageUrl || raw.image_url || raw.image || '',
        url,
        // 🔴 BE 원시 stock (2026-05-27). applyStorefrontStockCorrection 가 storefront 정량으로 min 보정.
        stock: Number.isFinite(stock) ? stock : Infinity,
      };
    },

    // ============ Promotion properties ============

    promotionCodeForCart(promo) {
      return String(promo?.code || promo?.id || promo?.name || '').trim();
    },

    // 🔴 _promotion_bxgy_anchor (2026-05-24) — "처음에 카트에서 프로모션 적용한 라인" 명시 flag.
    // 부모/자식 식별의 SSOT. role(buy/get) 가격 sort 결과나 cart DOM 순서 추론 X.
    // SSR (main-cart.liquid) + JS (applyBxgyGiftMarking) 모두 이 flag 우선해서 부모 표시.
    // 회귀 case: docs/policy/cart-coupon-hub.md §회귀 함정 — anchor 가 cheaper 라 get role 받으면 DOM 순서로 다른 라인이 부모로 잘못 마킹
    // 🔴 (2026-05-28) groupId 인자 추가 — 한 confirm 흐름의 anchor + 자식 공유 식별자.
    //   회귀 회피: 다른 anchor 의 같은 variant gy 와 properties 다름 → Shopify auto-merge 차단.
    //   `_` prefix 라 Shopify storefront 자동 hide + cleanup isPromoKey 자동 wipe.
    // 🔴 [중요] (2026-05-28) hidden(`_promotion_*`) 키만 박음. visible(`promotion_*`) 키 폐기.
    //   사유: visible 키는 (1) 체크아웃 line item property 로 노출 leak (2) migrate 가 매 로드마다
    //   /cart/change.js 로 떼어내며 라인 re-key → dataset.lineKey 꼬임 + 400 race 유발 (메인 standalone 오삭제 원인).
    //   BE Function 은 visible || hidden 둘 다 읽고 hidden fallback 있음
    //   (misto-msa cart_lines_discounts_generate_run.ts:108-133) → hidden 만으로 완전 동작.
    //   SSR Liquid 도 `_promotion_*` 만 읽음 (grep 검증). 회귀 case: wiki/docs/bxgy-visible-promotion-keys-removal-2026-05-28.md
    promotionProperties(promo, bxgyRole = '', anchor = false, addedByBxgy = false, groupId = '') {
      const code = Utils.promotionCodeForCart(promo);
      const properties = {
        _promotion_code: code,
        _promotion_id: promo.id || code,
        _promotion_title: promo.name || promo.title || code,
        _promotion_type: promo.type || '',
      };
      if (promo?.type === 'buy_x_get_y' && bxgyRole) {
        properties._promotion_bxgy_role = bxgyRole;
      }
      if (promo?.type === 'buy_x_get_y' && anchor) {
        properties._promotion_bxgy_anchor = 'true';
      }
      if (promo?.type === 'buy_x_get_y' && groupId) {
        properties._promotion_group_id = String(groupId);
      }
      // 🔴 _promotion_added_by_bxgy (2026-05-26) — "BXGY 카탈로그 add 로 새로 생성된 cart 라인" SSOT.
      // 사용자가 원래 카트에 갖고 있던 라인을 GY 로 선택한 경우엔 false (= flag 없음).
      // 해제·재선택 시 분기:
      //   addedByBxgy=true → cart 라인 자체 제거 (/cart/update.js qty=0)
      //   addedByBxgy=false → properties 만 clear (라인 정가 회복, 유지)
      if (addedByBxgy) {
        properties._promotion_added_by_bxgy = 'true';
      }
      return properties;
    },

    // ============ BXGY pure helpers (Phase 2a, 2026-05-27) ============
    // DOM read-only 또는 ctx 매개변수만 의존 — this.state 미참조.
    // 본문은 hub 의 동일 함수에서 그대로 이동 (로직 변경 0). hub.js 는 1줄 wrapper 유지.

    // 🔴 (2026-05-28) lineKey 만 사용. variant fallback 폐기 — 같은 variant 라인끼리 같은 selectionId 가 되어
    //   앵커/후보 식별이 섞이던 회귀 원인. SSR cart-promo-cell 은 항상 data-line-key 부여 → lineKey 누락 없음.
    //   회귀 case: wiki/docs/bxgy-unique-line-identity-2026-05-28.md
    bxgySelectionId(cell) {
      return cell?.dataset?.lineKey || '';
    },

    // 🔴 (2026-05-28) 자격 검사 — anchor cell qty 가 buyQty 배수인지.
    //   insufficient: qty < buyQty (예: 2+1 인데 qty=1)
    //   odd: qty % buyQty !== 0 (예: 2+1 인데 qty=3 → 짝수 필요)
    //   eligible: applicationCount = Math.floor(qty / buyQty)
    checkBxgyEligibility(cell, promo) {
      if (!promo || promo.type !== 'buy_x_get_y') return { eligible: true };
      const cartQty = Utils.bxgyLineQuantity(cell);
      const buyQty = Math.max(1, parseInt(promo.buyQty || '1', 10) || 1);
      if (cartQty < buyQty) {
        return { eligible: false, reason: 'insufficient', required: buyQty, current: cartQty };
      }
      if (cartQty % buyQty !== 0) {
        return { eligible: false, reason: 'odd', required: buyQty, current: cartQty };
      }
      return { eligible: true, applicationCount: Math.floor(cartQty / buyQty) };
    },

    bxgyLineQuantity(cell) {
      const quantity = Number(cell?.dataset?.lineQuantity || 1);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    },

    bxgySelectedIdsFromDom() {
      const modal = document.querySelector('[data-bxgy-modal]');
      if (!modal || modal.hidden) return [];
      return Array.from(modal.querySelectorAll('.bxgy-card.is-selected [data-bxgy-pick]'))
        .map((el) => el.dataset.bxgyPick)
        .filter(Boolean);
    },

    // 🔴 (2026-05-28) Y-only modal — selectedCandidates = Y 후보 (catalog) 만. anchor 는 modal 밖 (role='buy' 별도 처리).
    //   confirmBxgy 가 anchor 라인엔 직접 setLinePromotion(role='buy') 호출.
    //   본 함수는 catalog 신규 add 라인에 role 분배.
    //   🔴 multi-application 자동 max — required = applicationCount × getQty 만큼 'get'.
    //   ctx.required 우선, 없으면 promo.getQty fallback (단일 적용).
    bxgyRoleByLine(ctx, selectedCandidates) {
      const promoGetQty = Math.max(0, parseInt(ctx?.promotion?.getQty || '0', 10) || 0);
      const required = Math.max(promoGetQty, parseInt(ctx?.required || '0', 10) || 0);
      const roles = new Map();
      const lines = [];
      for (const c of (selectedCandidates || [])) {
        const id = c.id || (c.cell ? Utils.bxgySelectionId(c.cell) : '');
        if (!id) continue;
        const qty = c.source === 'cart'
          ? Utils.bxgyLineQuantity(c.cell)
          : Math.max(1, parseInt(ctx?.addQuantities?.[id] || c.quantity || '1', 10) || 1);
        const price = Utils.parseMoney(c.price || c.cell?.dataset?.linePrice || '0');
        for (let i = 0; i < qty; i++) {
          lines.push({ id, lineIndex: i, price });
        }
      }
      lines.sort((a, b) => a.price - b.price);
      lines.forEach((l, idx) => {
        roles.set(`${l.id}::${l.lineIndex}`, idx < required ? 'get' : 'buy');
      });
      return roles;
    },

    // selectionId 단위 role 매핑 (dataset.appliedPromotionRole 마킹용 — anchor 1 라인 한정).
    // 라인 단위 분배는 bxgyRoleByLine 사용. 본 함수는 선택 candidate 마다 단일 role 부여.
    // ============ DOM helpers (Phase I+M, 2026-05-27) ============

    // DOM read only — promo cell 묶음 식별. BXGY 는 sourceCell 의 group_id 묶음만 반환, 그 외 sourceCell 만.
    // 🔴 [중요] (2026-05-28) BXGY 는 group_id SSOT 로 스코프 (promotion_id 아님).
    //   promotion_id 로 잡으면 *같은 promo 를 쓰는 다른 anchor* 의 cells 까지 끌려와, 한 anchor 의 promo
    //   변경/해제 시 다른 anchor 의 promo 까지 풀림 (multi-anchor cross-clear 회귀).
    //   group_id = anchor + 자기 자식 gift 만 식별 (applyGiftMarking / SSR 3-pass 와 동일 SSOT).
    //   회귀 case: wiki/docs/bxgy-multi-anchor-same-promo-cross-clear-2026-05-28.md
    promotionCellsToClear(sourceCell, promotionId) {
      if (!sourceCell || !promotionId) return sourceCell ? [sourceCell] : [];
      const type = sourceCell.dataset.appliedPromotionType || '';
      if (type !== 'buy_x_get_y') return [sourceCell];
      const groupId = (sourceCell.dataset.appliedPromotionGroupId || '').trim();
      if (groupId) {
        return Array.from(document.querySelectorAll('[data-cart-promo-cell]')).filter(
          (cell) => (cell.dataset.appliedPromotionGroupId || '').trim() === groupId
        );
      }
      // legacy fallback — group_id 없는 구 cart 라인만 promotion_id 매칭 (점진 폐기).
      return Array.from(document.querySelectorAll('[data-cart-promo-cell]')).filter(
        (cell) => cell.dataset.appliedPromotionId === promotionId
      );
    },

    // dropdown-input custom element (assets/global.js) 의 update() 호출.
    // select.innerHTML 동적 갱신 후 dropdown UI 도 같이 재빌드. disabled 상태도 wrapper class 로 반영.
    refreshDropdownInput(select) {
      const wrap = select.closest('dropdown-input');
      if (!wrap) return;
      if (typeof wrap.update === 'function') wrap.update();
      wrap.classList.toggle('is-disabled', !!select.disabled);
    },

    // 같은 parentKey (= _promotion_id) 자식 gift 라인 DOM 제거.
    clearGiftRows(parentKey) {
      if (!parentKey) return;
      document
        .querySelectorAll(`.cart-item--gift[data-gift-parent="${CSS.escape(String(parentKey))}"]`)
        .forEach((el) => el.remove());
    },

    // cart promo cell 들의 line quantity 합산. BXGY revalidate / 자격 판정용.
    bxgyCartQuantity(cells) {
      const cellList = cells || Array.from(document.querySelectorAll('[data-cart-promo-cell]'));
      return cellList.reduce((sum, cell) => sum + Utils.bxgyLineQuantity(cell), 0);
    },

    bxgyRoleBySelectionId(ctx, selectedCandidates) {
      let remainingGetQty = Math.max(0, parseInt(ctx?.promotion?.getQty || '0', 10) || 0);
      const anchorId = ctx?.anchorSelectionId || '';
      const roles = new Map();
      if (anchorId) roles.set(anchorId, 'buy');
      const selected = [...(selectedCandidates || [])];
      const getCandidates = selected
        .filter((item) => {
          const id = item.id || (item.cell ? Utils.bxgySelectionId(item.cell) : '');
          return id && id !== anchorId;
        })
        .sort(
          (a, b) => Utils.parseMoney(a.price || a.cell?.dataset?.linePrice || '0') -
            Utils.parseMoney(b.price || b.cell?.dataset?.linePrice || '0')
        );
      getCandidates.forEach((item) => {
        const id = item.id || (item.cell ? Utils.bxgySelectionId(item.cell) : '');
        if (!id) return;
        const quantity = item.source === 'cart'
          ? Utils.bxgyLineQuantity(item.cell)
          : Math.max(1, parseInt(ctx?.addQuantities?.[id] || item.quantity || '1', 10) || 1);
        const role = remainingGetQty > 0 ? 'get' : 'buy';
        remainingGetQty = Math.max(0, remainingGetQty - quantity);
        roles.set(id, role);
      });
      selected.forEach((item) => {
        const id = item.id || (item.cell ? Utils.bxgySelectionId(item.cell) : '');
        if (id && !roles.has(id)) roles.set(id, 'buy');
      });
      return roles;
    },
  };

  global.CartCouponHubUtils = Utils;
})(typeof window !== 'undefined' ? window : globalThis);
